import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Terminal, BrainCircuit, Shield, Activity, DollarSign, Search, BarChart3, Globe, Database } from 'lucide-react';

const BOT_NAMES = [
    { name: "MACRO-EYE", role: "Global Events Scanner", icon: Globe, color: "text-secondary", bgColor: "bg-secondary/20", borderColor: "border-secondary/50" },
    { name: "ALPHA-GEN", role: "Strategy Generator", icon: BrainCircuit, color: "text-primary", bgColor: "bg-primary/20", borderColor: "border-primary/50" },
    { name: "RISK-GUARD", role: "Volatility Shield", icon: Shield, color: "text-accent", bgColor: "bg-accent/20", borderColor: "border-accent/50" },
    { name: "SENTI-V1", role: "Sentiment Engine", icon: Search, color: "text-secondary", bgColor: "bg-secondary/20", borderColor: "border-secondary/50" },
    { name: "WHALE-OS", role: "On-Chain Tracking", icon: Database, color: "text-primary", bgColor: "bg-primary/20", borderColor: "border-primary/50" },
    { name: "VOL-X", role: "Volume Vector", icon: BarChart3, color: "text-secondary", bgColor: "bg-secondary/20", borderColor: "border-secondary/50" }
];

const generateLog = (ticker) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 8);
    const actions = [
        `[SENTI-V1] Scraped 45,000+ tweets for ${ticker} | Sentiment: BULLISH (0.87)`,
        `[VOL-X] Detected abnormal volume spike (+400%) on Uniswap V3 Pool`,
        `[WHALE-OS] Tracking wallet 0x7a... moved 500 ETH to prediction pool`,
        `[RISK-GUARD] Calculating Sharpe Ratio... Adjusted Risk: LOW`,
        `[MACRO-EYE] Parsing Bloomberg terminal feed for "Regulatory News"`,
        `[ALPHA-GEN] Strategy DELTA_NEUTRAL_1 selected for execution`,
        `[SENTI-V1] Cross-referencing Reddit/Discord discussion velocity`,
        `[WHALE-OS] Analyzing holder distribution... Gini Coefficient: 0.45`
    ];
    return `${timestamp} > ${actions[Math.floor(Math.random() * actions.length)]}`;
};

import { usePaperTrading } from '../../contexts/PaperTradingContext';

export const AgentSimulator = ({ market, predictionResult }) => {
    const { balance, executeTrade } = usePaperTrading();
    const [logs, setLogs] = useState([]);
    const [stage, setStage] = useState('initializing');
    const [confidence, setConfidence] = useState(0);
    const [roi, setRoi] = useState(0);

    // Trade Confirmation Workflow States
    const [tradeStatus, setTradeStatus] = useState('idle'); // idle, initiating, activating, executing, confirmed

    useEffect(() => {
        // Reset state on market change
        setLogs([]);
        setStage('initializing');
        setConfidence(0);
        setRoi(0);
        setTradeStatus('idle');

        // Analysis Simulation Sequence
        let interval;
        const startAnalysis = async () => {
            await new Promise(r => setTimeout(r, 1000));
            setStage('analyzing');
            interval = setInterval(() => {
                setLogs(prev => [...prev.slice(-8), generateLog(market.title.substring(0, 10))]);
            }, 800);

            await new Promise(r => setTimeout(r, 4000));
            setStage('projection');

            setConfidence(87);
            setRoi(24.5);

            await new Promise(r => setTimeout(r, 2000));
            setStage('ready'); // Ready for user to click "Trade"
            // Keep logs running
        };

        startAnalysis();
        return () => clearInterval(interval);
    }, [market]);

    const handleTradeClick = async () => {
        setTradeStatus('initiating');

        // Frame 1: Initiating (Button loads)
        await new Promise(r => setTimeout(r, 1500));
        setTradeStatus('activating');

        // Frame 2: Agent Activation (Lobster zooms in / lights up)
        await new Promise(r => setTimeout(r, 2500));
        setTradeStatus('executing');

        // Frame 3: System Execution (Logs scroll fast)
        await new Promise(r => setTimeout(r, 2000));

        try {
            // Execute the trade against the virtual balance
            executeTrade(
                market,
                203.00, // Fixed amount for simulation or dynamic? Using $203 as per UI default
                predictionResult?.analysis?.side || 'YES',
                {
                    price: 0.5, // Default probability if no live data
                    predictionConfidence: confidence
                }
            );
            setTradeStatus('confirmed');
        } catch (error) {
            console.error("Trade failed:", error);
            // Could add error state here, but for now reset or show alert
            alert(`Trade Failed: ${error.message}`);
            setTradeStatus('idle');
        }
    };

    return (
        <div className="flex-1 bg-black/90 relative overflow-hidden flex flex-col h-full border border-white/10 rounded-none md:rounded-2xl m-0 md:m-4">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-20 p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-b from-black via-black/80 to-transparent">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${tradeStatus === 'idle' || tradeStatus === 'ready' ? 'bg-secondary animate-pulse shadow-[0_0_10px_#00d4aa]' : 'bg-primary animate-ping shadow-[0_0_10px_#ff4d4d]'}`} />
                        <span className="text-[10px] md:text-xs font-mono text-secondary uppercase tracking-[0.3em] font-bold">
                            {tradeStatus === 'idle' || tradeStatus === 'ready' ? 'LOBSTER_LIVE' : 'PROTOCOL_EXECUTION'}
                        </span>
                    </div>
                    <h1 className="text-lg md:text-2xl font-display font-black max-w-2xl text-white line-clamp-2 uppercase tracking-tight">{market.title}</h1>
                </div>

                {/* Requirements: Virtual Bonus & Gauges */}
                <div className="flex gap-4 md:gap-8 items-center">
                    <div className="hidden md:flex gap-6">
                        <div className="text-center">
                            <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-2 font-mono">Compute</div>
                            <div className="relative w-12 h-12">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" fill="transparent" className="text-white/5" />
                                    <motion.circle
                                        cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" fill="transparent"
                                        className="text-primary"
                                        strokeDasharray="125.6"
                                        animate={{ strokeDashoffset: [125.6, 125.6 * (1 - 0.65)] }}
                                        transition={{ duration: 2, ease: "easeOut" }}
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold">65%</div>
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-2 font-mono">Signal</div>
                            <div className="relative w-12 h-12">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" fill="transparent" className="text-white/5" />
                                    <motion.circle
                                        cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" fill="transparent"
                                        className="text-secondary"
                                        strokeDasharray="125.6"
                                        animate={{ strokeDashoffset: [125.6, 125.6 * (1 - 0.87)] }}
                                        transition={{ duration: 2, ease: "easeOut" }}
                                    />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold">87%</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-xl p-2 md:p-3 text-right backdrop-blur-xl">
                        <div className="text-[9px] md:text-[10px] text-secondary font-bold uppercase tracking-wider">Virtual Balance</div>
                        <div className="text-base md:text-2xl font-mono font-bold text-white tracking-tighter">
                            ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Visualizer Area (Lobster Nodes) */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                {/* Central Target Node */}
                <motion.div
                    animate={{
                        scale: tradeStatus === 'activating' ? [1, 1.2, 1] : [1, 1.05, 1],
                        boxShadow: tradeStatus === 'activating'
                            ? ["0 0 20px rgba(220,38,38,0.2)", "0 0 100px rgba(220,38,38,0.8)", "0 0 20px rgba(220,38,38,0.2)"]
                            : ["0 0 20px rgba(0,229,255,0.2)", "0 0 50px rgba(0,229,255,0.5)", "0 0 20px rgba(0,229,255,0.2)"]
                    }}
                    transition={{ duration: tradeStatus === 'activating' ? 0.5 : 2, repeat: Infinity }}
                    className={`w-32 h-32 rounded-full border-2 flex items-center justify-center z-20 relative bg-black ${tradeStatus === 'activating' ? 'border-primary' : 'border-secondary'}`}
                >
                    {tradeStatus === 'confirmed' ? (
                        <Check className="w-16 h-16 text-secondary" />
                    ) : (
                        <div className="text-center">
                            <div className="text-[10px] text-muted-foreground uppercase">Target</div>
                            <div className="text-xl font-bold text-white">$203.00</div>
                        </div>
                    )}
                </motion.div>

                {/* Orbiting Bot Nodes */}
                {BOT_NAMES.map((bot, i) => {
                    const angle = (i * 60);
                    return (
                        <BotNode
                            key={i}
                            index={i}
                            angle={angle}
                            bot={bot}
                            isExecuting={tradeStatus === 'activating' || tradeStatus === 'executing'}
                        />
                    )
                })}

                {/* Background Grid/Effects */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />

                {/* Molty Mascot Floating (Colorful) */}
                <motion.div
                    animate={{
                        y: [0, -20, 0],
                        rotate: [0, 5, -5, 0]
                    }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute right-10 bottom-10 w-24 h-24 opacity-80 pointer-events-none overflow-hidden drop-shadow-[0_0_10px_rgba(0,212,170,0.3)]"
                >
                    <img src="/claw_mascot.png" alt="" className="w-full h-full object-contain" />
                </motion.div>

                {tradeStatus === 'confirmed' && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} className="absolute inset-0 bg-secondary/10 z-0" />
                )}
            </div>

            {/* Terminal & Actions */}
            <div className="h-auto md:h-72 border-t border-white/10 bg-black/80 backdrop-blur-md grid grid-cols-1 lg:grid-cols-3">
                {/* Terminal */}
                <div className="col-span-1 lg:col-span-2 p-3 md:p-4 font-mono text-sm lg:border-r border-white/10 overflow-hidden flex flex-col bg-[#050505] h-48 md:h-auto relative">
                    {/* Scanline Effect */}
                    <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.02),rgba(0,255,0,0.01),rgba(0,0,255,0.02))] bg-[size:100%_2px,300%_100%] z-20" />

                    <div className="flex items-center justify-between text-muted-foreground mb-2 pb-2 border-b border-white/5 relative z-10">
                        <div className="flex items-center gap-2">
                            <Terminal className="w-3 h-3 md:w-4 md:h-4 text-secondary" />
                            <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] font-bold">Node_Telemetry_v4.0.1</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] text-white/20 font-mono">MOLTY_ACTIVE: TRUE</span>
                            {tradeStatus === 'executing' && <span className="text-primary animate-pulse font-black text-[10px] tracking-tighter">PHASE_2: ESTABLISHING_VECTOR</span>}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-1 font-mono text-[9px] md:text-[11px] relative z-10 scrollbar-hide">
                        <AnimatePresence>
                            {logs.map((log, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -5 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-secondary/80 flex gap-2"
                                >
                                    <span className="text-white/20">[{i}]</span>
                                    <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{log}</span>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                        {(stage === 'analyzing' || tradeStatus === 'executing') && (
                            <div className="flex items-center gap-2 text-primary">
                                <span className="text-white/20">[*]</span>
                                <span className="animate-pulse">_PROCESSING_INBOUND_STREAM...</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Interface (Module 5) */}
                <div className="p-4 md:p-6 flex flex-col justify-center gap-4 bg-[#0A0A0A]">
                    {tradeStatus === 'idle' || tradeStatus === 'ready' ? (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <div className="flex justify-between text-xs text-muted-foreground font-mono uppercase">
                                    <span>Analysis Progress</span>
                                    <span>{stage === 'ready' ? '100%' : '65%'}</span>
                                </div>
                                <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-primary"
                                        initial={{ width: "0%" }}
                                        animate={{ width: stage === 'ready' ? '100%' : '65%' }}
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleTradeClick}
                                disabled={stage !== 'ready'}
                                className={`w-full py-5 rounded-xl font-black text-sm transition-all shadow-2xl flex flex-col items-center justify-center gap-1 uppercase tracking-widest relative overflow-hidden group
                                    ${stage === 'ready'
                                        ? 'bg-primary text-white hover:bg-primary/90 hover:shadow-primary/40'
                                        : 'bg-white/5 text-muted-foreground cursor-not-allowed'}`}
                            >
                                <span className="relative z-10">{stage === 'ready' ? 'Initialize Lobster Execution' : 'Analyzing Neural Flow...'}</span>
                                {stage === 'ready' && <span className="text-[8px] opacity-70 font-mono">SECURE_ONCHAIN_HANDSHAKE_READY</span>}
                                {stage === 'ready' && <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />}
                            </button>
                        </div>
                    ) : (
                        <div className="text-center space-y-4">
                            {tradeStatus === 'confirmed' ? (
                                <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }}>
                                    <div className="mx-auto w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mb-4 ring-2 ring-secondary">
                                        <Check className="w-8 h-8 text-secondary" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-1">Position Opened</h3>
                                    <p className="text-sm text-secondary">Success! View in Portfolio.</p>
                                </motion.div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="mx-auto w-12 h-12 rounded-full border-2 border-red-500/50 border-t-red-500 animate-spin" />
                                    <p className="text-red-500 font-bold animate-pulse">
                                        {tradeStatus === 'initiating' && 'Initiating Protocol...'}
                                        {tradeStatus === 'activating' && 'Activating Lobster Nodes...'}
                                        {tradeStatus === 'executing' && 'Establishing Secure Connection...'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const BotNode = ({ index, angle, bot, isExecuting }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{
                opacity: 1,
                rotate: isExecuting ? 360 : 0 // Spin faster on execution maybe?
            }}
            // Orbit Animation
            style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '400px', // Orbit diameter
                height: '400px',
                marginLeft: '-200px',
                marginTop: '-200px',
            }}
        >
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: isExecuting ? 5 : 20, repeat: Infinity, ease: "linear" }}
                className="w-full h-full relative"
            >
                <div
                    className={`absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center transform -rotate-0`}
                    style={{ transformOrigin: '50% 200px', transform: `rotate(${angle}deg)` }} // Fixed positions for now or dynamic? Let's use the rotation wrapper
                >
                    {/* Actually, simplified Approach: Rotate the PARENT container, and counter-rotate the child so icon stays upright */}
                    {/* Re-doing orbit logic to be simpler: Just simple CSS animation on a wrapper */}
                </div>
            </motion.div>

            {/* Let's try a simpler approach for Orbiting Nodes without complex math */}
            <OrbitingItem angle={angle} radius={180} duration={isExecuting ? 10 : 40} delay={index}>
                <div className={`p-3 rounded-xl bg-black border backdrop-blur-md flex flex-col items-center gap-2 min-w-[100px] transition-all duration-500 ${isExecuting ? 'border-red-500 shadow-[0_0_15px_rgba(220,38,38,0.5)]' : `${bot.borderColor} hover:shadow-[0_0_10px] hover:shadow-current`}`}>
                    <bot.icon className={`w-6 h-6 ${isExecuting ? 'text-red-500' : bot.color}`} />
                    <div className={`text-[9px] font-bold uppercase tracking-wider ${isExecuting ? 'text-white' : bot.color}`}>{bot.name}</div>
                    <div className="text-[8px] text-gray-500 text-center leading-tight">{bot.role}</div>
                </div>
            </OrbitingItem>
        </motion.div>
    )
}

const OrbitingItem = ({ children, radius, duration, angle }) => {
    return (
        <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: duration, repeat: Infinity, ease: "linear" }}
            style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                pointerEvents: 'none'
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)` // Initial offset
                }}
            >
                <motion.div
                    animate={{ rotate: -360 }} // Counter-rotate to keep upright
                    transition={{ duration: duration, repeat: Infinity, ease: "linear" }}
                    className="pointer-events-auto"
                >
                    {children}
                </motion.div>
            </div>
        </motion.div>
    )
}
